// frontend/src/tabs/TabsHost.jsx
//
// Renders every open tab inside a TabPane, keeping the inactive ones mounted
// and hidden. App.jsx renders it in place of the react-router <Outlet/>.
//
// A tab mounts the first time it is active. Tabs restored from a previous
// session come back all at once, and a tab the merchant has not looked at
// yet has no state to keep alive — mounting it would load its page and its
// grid for nobody. A hidden tab keeps its queries and its grid; a save made
// elsewhere reaches them through `queries/dataChanged`.
//
// Each tab is wrapped in TabOverlayScope, so its overlays portal into the
// tab's subtree instead of document.body, and hide with it.
//
// TabParamsContext carries route params matched against the TAB's path, not
// the browser URL, so a hidden tab reads its own params; pages read
// useTabParams().
//
// This component drives activeId → URL. URL → activeId is TabStrip's (it
// reacts to react-router's location changes).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useTabs } from './useTabs';
import { TabIdContext, TabParamsContext } from './tabsContexts';
import { findElementForPath, paramsForTabPath } from './routeRegistry';
import { TabPane } from './TabPane';
import TabOverlayScope from './TabOverlayScope';

function Tab({ tab, isActive }) {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(isActive);
  if (isActive && !mounted) setMounted(true);
  // tab.path is immutable per tab id (set at OPEN time, never mutated),
  // so the matched params are stable for the lifetime of the tab and
  // useMemo on tab.path is sufficient — no parameters-change race.
  const params = useMemo(() => paramsForTabPath(tab.path), [tab.path]);
  const element = findElementForPath(tab.path);
  if (!mounted) return null;
  return (
    <TabPane active={isActive}>
      <TabIdContext.Provider value={tab.id}>
        <TabParamsContext.Provider value={params}>
          <TabOverlayScope>
            {element || (
              <div style={{ padding: 24 }}>
                {t('tabs.pageNotFound', { path: tab.path })}
              </div>
            )}
          </TabOverlayScope>
        </TabParamsContext.Provider>
      </TabIdContext.Provider>
    </TabPane>
  );
}

export default function TabsHost() {
  const { tabs, activeId } = useTabs();
  const navigate = useNavigate();
  const location = useLocation();

  const activeTab = useMemo(
    () => tabs.find((tab) => tab.id === activeId) || null,
    [tabs, activeId],
  );

  // activeId → URL. Fires on every activeId change (user click on a tab,
  // programmatic focus, close-falls-back-to-neighbor, closeAllExceptHome).
  //
  // Skip on initial mount: the URL is the source of truth when the user
  // arrives via deep link / SSO. TabStrip's URL→activeId effect runs on
  // the same commit and will pull the active tab into sync with the URL.
  // Without this skip, the two effects would race — TabsHost pushing URL
  // to the (stale) initial activeId, TabStrip pushing activeId to the
  // (real) URL — and oscillate for several renders before settling at
  // whichever effect happened to win the last commit.
  const isFirstSyncRef = useRef(true);
  useEffect(() => {
    if (isFirstSyncRef.current) {
      isFirstSyncRef.current = false;
      return;
    }
    if (!activeTab) return;
    if (location.pathname !== activeTab.path) {
      navigate(activeTab.path);
    }
    // location.pathname intentionally omitted from deps: we only want to
    // push the URL when the *tab* changes, not when location updates for
    // any reason. The TabStrip URL→focus effect handles the reverse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab?.id, activeTab?.path, navigate]);

  return (
    <>
      {tabs.map((tab) => (
        <Tab key={tab.id} tab={tab} isActive={tab.id === activeId} />
      ))}
    </>
  );
}
